import { ParsedAddress, clevertapEvents } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';

export const loadGoogleMapsScript = () => {
  const googleCloudId = process.env.REACT_APP_GOOGLE_CLOUD_ID;
  // Check if script is already loaded
  const existingScript = document.getElementById('googleMapsScript');
  if (existingScript) {
    // If the script is already loaded, do nothing
    logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
      'Google Maps Script Already Present': true
    });
    return;
  }

  // Create a new script element
  const script = document.createElement('script');
  script.id = 'googleMapsScript';
  script.src = `https://maps.googleapis.com/maps/api/js?key=${googleCloudId}&libraries=places&v=weekly&loading=async`;
  script.async = true;

  // Append the script to the document head
  document.head.appendChild(script);
  logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
    'Google Maps Script Loaded': true
  });
};

export type PlacesAutocompleteClients = {
  autocomplete: google.maps.places.AutocompleteService;
  places: google.maps.places.PlacesService;
  sessionToken: google.maps.places.AutocompleteSessionToken;
};

/** Returns Places clients or null if the Maps JS `places` library is not available yet. */
export const createPlacesAutocompleteClients = (): PlacesAutocompleteClients | null => {
  if (typeof window === 'undefined' || !window.google?.maps?.places) return null;
  return {
    autocomplete: new window.google.maps.places.AutocompleteService(),
    places: new window.google.maps.places.PlacesService(document.createElement('div')),
    sessionToken: new window.google.maps.places.AutocompleteSessionToken()
  };
};

/**
 * Runs `onReady` once when `google.maps.places` is available (handles async script load).
 * Returns cleanup for listeners and polling.
 */
export const whenGooglePlacesReady = (onReady: () => void): (() => void) => {
  let done = false;

  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    onReady();
    return () => undefined;
  }

  const complete = () => {
    if (done || !window.google?.maps?.places) return;
    done = true;
    window.clearInterval(pollId);
    onReady();
  };

  const pollId = window.setInterval(complete, 200);
  const script = document.getElementById('googleMapsScript') as HTMLScriptElement | null;
  const handleScriptLoad = () => complete();
  if (script) {
    script.addEventListener('load', handleScriptLoad);
  }

  const timeoutId = window.setTimeout(() => {
    window.clearInterval(pollId);
    if (!done && !window.google?.maps?.places) {
      logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
        'Google Maps Script Loaded': false,
        reason: 'timeout'
      });
    }
  }, 60_000);

  return () => {
    script?.removeEventListener('load', handleScriptLoad);
    window.clearInterval(pollId);
    window.clearTimeout(timeoutId);
  };
};

export const googleAdressParser = addressComponents => {
  const address: ParsedAddress = { apartment: '', city: '', country: '', state: '', street: '', zipCode: '' };

  if (!Array.isArray(addressComponents)) {
    return;
  }

  if (!addressComponents.length) return;

  for (let i = 0; i < addressComponents.length; i += 1) {
    const component = addressComponents[i];

    if (component.types.includes('street_number')) {
      address.street = component.long_name;
    }
    if (component.types.includes('route')) {
      address.street = address.street ? `${address.street} ${component.long_name}` : component.long_name;
    }
    // city = locality > administrative_area_level_3 > administrative_area_level_2
    if (!address.city) {
      if (component.types.includes('locality')) {
        address.city = component.long_name;
      } else if (component.types.includes('administrative_area_level_3')) {
        address.city = component.long_name;
      } else if (component.types.includes('administrative_area_level_2')) {
        address.city = component.long_name;
      }
    }

    if (component.types.includes('country')) {
      address.country = component.short_name;
    }

    if (component.types.includes('administrative_area_level_1')) {
      address.state = component.short_name;
    }

    if (component.types.includes('postal_code')) {
      address.zipCode = component.long_name;
    }
  }
  return address;
};
